/**
 * GitLab enrichment data types for frontend display
 * These match the simplified structures stored in entity annotations
 */

/**
 * Languages used in the repository
 * Key is language name, value is percentage
 */
export interface GitLabLanguages {
  [language: string]: number;
}

/**
 * Environment information from GitLab
 */
export interface GitLabEnvironment {
  id: number;
  name: string;
  externalUrl: string | null;
  lastDeployment?: {
    status: string;
    createdAt: string;
    finishedAt: string | null;
    commitShortId: string;
    username: string;
    pipelineId: number | null;
    pipelineUrl: string | null;
  };
}

/**
 * Release information from GitLab
 */
export interface GitLabRelease {
  tagName: string;
  name: string;
  releasedAt: string;
  description: string;
}

/**
 * Annotation keys for GitLab enrichment data
 */
export const GITLAB_ANNOTATIONS = {
  LANGUAGES: 'kpc.io/gitlab-languages',
  ENVIRONMENTS: 'kpc.io/gitlab-environments',
  RELEASES: 'kpc.io/gitlab-releases',
  ENRICHED_AT: 'kpc.io/gitlab-enriched-at',
} as const;

/**
 * Parse GitLab languages annotation
 */
export function parseLanguagesAnnotation(
  annotation: string | undefined,
): GitLabLanguages | null {
  if (!annotation) return null;
  try {
    return JSON.parse(annotation) as GitLabLanguages;
  } catch {
    return null;
  }
}

/**
 * Parse GitLab environments annotation
 */
export function parseEnvironmentsAnnotation(
  annotation: string | undefined,
): GitLabEnvironment[] | null {
  if (!annotation) return null;
  try {
    return JSON.parse(annotation) as GitLabEnvironment[];
  } catch {
    return null;
  }
}

/**
 * Parse GitLab releases annotation
 */
export function parseReleasesAnnotation(
  annotation: string | undefined,
): GitLabRelease[] | null {
  if (!annotation) return null;
  try {
    return JSON.parse(annotation) as GitLabRelease[];
  } catch {
    return null;
  }
}
